import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FullScreenSpinner } from '../../components/ui/Spinner';
import EmptyState from '../../components/ui/EmptyState';
import PageHeader from '../../components/layout/PageHeader';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import DatePicker from '../../components/ui/DatePicker';
import Textarea from '../../components/ui/Textarea';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import CategoryBadge from '../../components/candidates/CategoryBadge';
import LeadDocumentsPanel from '../../components/leads/LeadDocumentsPanel';
import { usePermission } from '../../auth/usePermission';
import { useAuth } from '../../auth/AuthContext';
import { getLead, updateLead, deleteLead, convertLead } from '../../api/leads';
import { listLeadSources, listNationalities } from '../../api/masters';
import { pushToast } from '../../components/ui/toastBus';
import { formatValidationErrors } from '../../utils/errors';

const FIELD_LABELS = {
  email: 'Email',
  contact_no: 'Contact No',
  whatsapp_no: 'WhatsApp No',
  home_country_contact_no: 'Home Country Contact',
  nationality: 'Nationality',
  date_of_birth: 'Date of Birth',
  passport_no: 'Passport No',
  passport_expiry_date: 'Passport Expiry',
  old_emirates_id_no: 'Old Emirates ID No',
  referred_agent_name: "Referred Agent's Name",
  visa_type: 'Visa Type',
  visa_validity: 'Visa Validity',
  uid_no: 'UID No',
  uae_license_no: 'UAE DL No',
  uae_license_expiry: 'UAE DL Expiry',
  home_license_no: 'Home/GCC DL No',
  home_license_expiry: 'Home/GCC DL Expiry',
};

function ReadOnlySheet({ lead }) {
  const fields = Object.keys(FIELD_LABELS).filter((key) => lead[key]);

  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
      {fields.map((key) => (
        <div key={key}>
          <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">{FIELD_LABELS[key]}</dt>
          <dd className="text-sm text-gray-800">{String(lead[key])}</dd>
        </div>
      ))}
      {fields.length === 0 && <p className="col-span-2 text-sm text-gray-400">No profile details captured yet.</p>}
      {lead.notes && (
        <div className="col-span-2">
          <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">Notes</dt>
          <dd className="text-sm text-gray-800 whitespace-pre-wrap">{lead.notes}</dd>
        </div>
      )}
    </dl>
  );
}

function LeadProfileForm({ lead }) {
  const queryClient = useQueryClient();
  const sourcesQuery = useQuery({ queryKey: ['lead-sources'], queryFn: () => listLeadSources() });
  const nationalitiesQuery = useQuery({ queryKey: ['nationalities'], queryFn: () => listNationalities() });
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(() => ({ ...lead }));
  const [errors, setErrors] = useState({});

  const mutation = useMutation({
    mutationFn: (payload) => updateLead(lead.id, payload),
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Lead updated.' });
      queryClient.invalidateQueries({ queryKey: ['leads', String(lead.id)] });
      setEditing(false);
      setErrors({});
    },
    onError: (err) => setErrors(err.response?.data?.errors || {}),
  });

  const set = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    mutation.mutate(form);
  };

  const fieldError = (field) => errors[field]?.[0];

  const passportWarning =
    form.passport_expiry_date &&
    new Date(form.passport_expiry_date) < new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);

  if (!editing) {
    return (
      <div>
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span className="text-xs uppercase tracking-wide text-gray-400">Source</span>
            <span>{lead.source || '—'}</span>
          </div>
          <Button
            variant="secondary"
            onClick={() => {
              setForm({ ...lead });
              setEditing(true);
            }}
          >
            Edit profile
          </Button>
        </div>
        <ReadOnlySheet lead={lead} />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Input label="Full Name" required value={form.full_name} onChange={set('full_name')} error={fieldError('full_name')} className="col-span-2" />
        <Input label="Contact No" required value={form.contact_no} onChange={set('contact_no')} error={fieldError('contact_no')} />
        <Select label="Source" value={form.lead_source_id || ''} onChange={set('lead_source_id')}>
          <option value="">Select…</option>
          {(sourcesQuery.data || []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <Input label="Email" type="email" value={form.email || ''} onChange={set('email')} error={fieldError('email')} />
        <Input label="WhatsApp No" value={form.whatsapp_no || ''} onChange={set('whatsapp_no')} />
        <Input label="Home Country Contact" value={form.home_country_contact_no || ''} onChange={set('home_country_contact_no')} />
        <Select label="Nationality" value={form.nationality_id || ''} onChange={set('nationality_id')} error={fieldError('nationality_id')}>
          <option value="">Select…</option>
          {(nationalitiesQuery.data || []).map((n) => (
            <option key={n.id} value={n.id}>
              {n.name}
            </option>
          ))}
        </Select>
        <DatePicker label="Date of Birth" value={form.date_of_birth?.slice(0, 10) || ''} onChange={set('date_of_birth')} error={fieldError('date_of_birth')} />
        <Input label="Passport No" value={form.passport_no || ''} onChange={set('passport_no')} error={fieldError('passport_no')} />
        <div>
          <DatePicker
            label="Passport Expiry"
            value={form.passport_expiry_date?.slice(0, 10) || ''}
            onChange={set('passport_expiry_date')}
            error={fieldError('passport_expiry_date')}
          />
          {passportWarning && (
            <div className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <p>Passport expires in less than 1 year — sending to Candidate will be blocked until this is resolved.</p>
              <label className="mt-1 flex items-center gap-1.5">
                <input type="checkbox" checked={!!form.passport_expiry_overridden} onChange={set('passport_expiry_overridden')} />
                Allow sending anyway
              </label>
            </div>
          )}
        </div>
        <Input label="Old Emirates ID No" value={form.old_emirates_id_no || ''} onChange={set('old_emirates_id_no')} />
        <Input label="Referred Agent's Name" value={form.referred_agent_name || ''} onChange={set('referred_agent_name')} />
      </div>

      {lead.category?.location === 'inside_uae' && (
        <div className="grid grid-cols-2 gap-4 border-t border-gray-100 pt-4">
          <Select label="Visa Type" value={form.visa_type || ''} onChange={set('visa_type')}>
            <option value="">Select…</option>
            <option value="visit">Visit</option>
            <option value="employment">Employment</option>
          </Select>
          <DatePicker label="Visa Validity" value={form.visa_validity?.slice(0, 10) || ''} onChange={set('visa_validity')} />
          <Input label="UID No" value={form.uid_no || ''} onChange={set('uid_no')} />
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 border-t border-gray-100 pt-4">
        {lead.category?.license_type === 'uae' && (
          <>
            <Input label="UAE DL No" value={form.uae_license_no || ''} onChange={set('uae_license_no')} />
            <DatePicker label="UAE DL Expiry" value={form.uae_license_expiry?.slice(0, 10) || ''} onChange={set('uae_license_expiry')} />
          </>
        )}
        {lead.category?.license_type === 'gcc_home' && (
          <>
            <Input label="Home/GCC DL No" value={form.home_license_no || ''} onChange={set('home_license_no')} />
            <DatePicker label="Home/GCC DL Expiry" value={form.home_license_expiry?.slice(0, 10) || ''} onChange={set('home_license_expiry')} />
          </>
        )}
      </div>

      <div className="border-t border-gray-100 pt-4">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <input type="checkbox" checked={!!form.has_rta_permit} onChange={set('has_rta_permit')} />
          Holds an RTA permit with Dubai companies?
        </label>
        {form.has_rta_permit && (
          <Textarea className="mt-2" value={form.rta_permit_details || ''} onChange={set('rta_permit_details')} />
        )}
      </div>

      <Textarea label="Notes" value={form.notes || ''} onChange={set('notes')} />

      <div className="flex justify-end gap-3">
        <Button type="button" variant="secondary" onClick={() => setEditing(false)}>
          Cancel
        </Button>
        <Button type="submit" loading={mutation.isPending}>
          Save changes
        </Button>
      </div>
    </form>
  );
}

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = usePermission();
  const { user } = useAuth();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);

  const leadQuery = useQuery({ queryKey: ['leads', id], queryFn: () => getLead(id) });

  const deleteMutation = useMutation({
    mutationFn: () => deleteLead(id),
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Lead deleted.' });
      navigate('/leads');
    },
    onError: (err) => {
      pushToast({ type: 'error', message: err.response?.data?.message || 'Could not delete lead.' });
      setDeleteOpen(false);
    },
  });

  const sendMutation = useMutation({
    mutationFn: () => convertLead(id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['leads', id] });
      const missing = result.missing_documents;
      pushToast({
        type: missing?.length ? 'error' : 'success',
        message: missing?.length
          ? `Sent to candidate ${result.candidate.reference_no}, but ${missing.length} document(s) are still missing.`
          : `Sent to candidate ${result.candidate.reference_no}.`,
      });
      navigate(`/candidates/${result.candidate.id}`);
    },
    onError: (err) => {
      setSendOpen(false);
      if (err.response?.data?.errors) {
        pushToast({ type: 'error', message: `Complete the profile before sending — ${formatValidationErrors(err.response.data.errors)}` });
      } else {
        pushToast({ type: 'error', message: err.response?.data?.message || 'Could not send lead.' });
      }
    },
  });

  if (leadQuery.isLoading) return <FullScreenSpinner />;
  if (leadQuery.isError) return <EmptyState title="Lead not found" />;

  const lead = leadQuery.data;
  const isOwner = lead.created_by_id === user?.id;
  const canSend = lead.status === 'new' && (can('leads.send-all') || (can('leads.send-own') && isOwner));
  const canDelete = can('leads.delete-all') || (can('leads.delete-own') && isOwner);
  const editable = lead.status === 'new';

  return (
    <div>
      <PageHeader
        title={lead.full_name}
        description={lead.reference_no}
        actions={
          <>
            {canDelete && (
              <Button variant="secondary" onClick={() => setDeleteOpen(true)}>
                Delete
              </Button>
            )}
            {canSend && (
              <Button onClick={() => setSendOpen(true)}>Send to Candidate</Button>
            )}
          </>
        }
      />

      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={lead.status === 'converted' ? 'green' : 'gray'}>{lead.status}</Badge>
          <CategoryBadge category={lead.category} />
        </div>

        {lead.conversion && (
          <div className="mt-3 rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">
            Converted to candidate{' '}
            <Link to={`/candidates/${lead.conversion.candidate_id}`} className="font-medium underline">
              {lead.conversion.candidate_reference_no}
            </Link>{' '}
            by {lead.conversion.converted_by} on {new Date(lead.conversion.converted_at).toLocaleString()}.
            {lead.conversion.missing_documents?.length > 0 && (
              <> Still missing: {lead.conversion.missing_documents.join(', ')} — add it from the candidate's profile.</>
            )}
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-gray-100 pt-3 text-sm">
          <div>
            <span className="text-xs uppercase tracking-wide text-gray-400">Created By</span>{' '}
            <span className="text-gray-800">{lead.created_by}</span>
          </div>
          <div>
            <span className="text-xs uppercase tracking-wide text-gray-400">Created</span>{' '}
            <span className="text-gray-800">{new Date(lead.created_at).toLocaleDateString()}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            {editable ? <LeadProfileForm lead={lead} /> : <ReadOnlySheet lead={lead} />}
          </div>
        </div>

        <div>
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h3 className="mb-3 text-sm font-semibold text-gray-900">Required Documents</h3>
            <LeadDocumentsPanel leadId={lead.id} requiredCodes={lead.required_documents} documents={lead.documents} editable={editable} />
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => deleteMutation.mutate()}
        title="Delete lead"
        message={`Delete ${lead.full_name} (${lead.reference_no})? This cannot be undone.`}
        confirmLabel="Delete"
        loading={deleteMutation.isPending}
      />

      <ConfirmDialog
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        onConfirm={() => sendMutation.mutate()}
        title="Send to Candidate"
        message={`Send ${lead.full_name} (${lead.reference_no}) to Candidate? This creates a candidate record and starts the workflow.`}
        confirmLabel="Send"
        variant="primary"
        loading={sendMutation.isPending}
      />
    </div>
  );
}

